import cx from "classnames";
import { useState } from "react";

import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import type { ClaimsDataType } from "types/DataTypes";
import {
  useGetClaimByIdQuery,
  useArchiveClaimMutation,
} from "services/claims-api.ts";
import { setShowArchiveModal } from "reducers/claims";
import "./ArchiveModal.scss";
import { useDispatch } from "utils/redux";
import { useNavigate } from "react-router-dom";

interface ArchiveClaimModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimId: number;
}

const ArchiveClaimModal = ({
  isOpen,
  onClose,
  claimId,
}: ArchiveClaimModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: claim, isLoading } = useGetClaimByIdQuery<{
    data: ClaimsDataType;
    isLoading: boolean;
  }>({ id: claimId.toString() }, { skip: !claimId });

  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archiveClaim] = useArchiveClaimMutation();
  const handleConfirmArchive = async () => {
    try {
      await archiveClaim({
        claimId: claimId,
      }).unwrap();
      dispatch(setShowArchiveModal(false));
      navigate("/section-111/claims");
    } catch (error) {
      setArchiveError("Failed to archive claim");
    }
  };
  if (isLoading) {
    return null;
  }

  return (
    <div
      className={cx("archive-modal overlay overlay-slidedown", {
        open: isOpen,
      })}
    >
      <div className="modal-content">
        <div className="grid-layout gl-1 modal-grid-wrapper">
          {claim?.can_archive_claim ? (
            <div className="data-block no-hover">
              <div className="data-block-header flex ai-c jc-sb">
                Confirm Archive
                <ButtonIcon kind="neutral" onClick={onClose}>
                  close
                </ButtonIcon>
              </div>
              <div className="delete-confirmation-text">
                <h2>Are you sure you want to archive this claim?</h2>
                <p>
                  By archiving this claim it will no longer be accessible in the
                  UI and all reporting to CMS will stop. The system will attempt
                  to do a final query after marking as <b>Archived</b> if it is
                  valid to Query. Otherwise, the final query will be skipped.
                </p>
              </div>
              <div className="error">{archiveError}</div>
              <div className="flex jc-c ai-c mt-1">
                <Button
                  className="delete-button"
                  onClick={handleConfirmArchive}
                >
                  Archive
                </Button>
                <Button onClick={onClose}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="data-block no-hover">
              <div className="data-block-header flex ai-c jc-sb">
                Archive Disabled
                <ButtonIcon kind="neutral" onClick={onClose}>
                  close
                </ButtonIcon>
              </div>
              <div className="delete-confirmation-text">
                <p>This claim is not valid for archival.</p>
                <p>
                  In order for a claim to be archived it cannot be in a
                  reporting status and it can not have any open Query or Claim
                  file responses.
                </p>
              </div>

              <div className="flex jc-c ai-c gap-1 mt-1">
                <Button onClick={onClose}>Close</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchiveClaimModal;
